import React from 'react';
import { hero, apps, testimonial, closingCta, faqs, PCOServicesIcon, ProductHuntBadge } from './content';
import { usePricing } from './usePricing';
import { useExperiment } from '../../../experiments/useExperiment';
import { trackExperiment } from '../../../experiments/track';
import ctaGradient from '../assets/cta_gradient.jpg';
import featuresGradient from '../assets/features_gradient.jpg';

function Container({ className = '', children }) {
  return <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;
}

function Underline({ fillClass = 'fill-blue-300/70' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 418 42"
      className={`absolute top-2/3 left-0 h-[0.58em] w-full ${fillClass}`}
      preserveAspectRatio="none"
    >
      <path d="M203.371.916c-26.013-2.078-76.686 1.963-124.73 9.946L67.3 12.749C35.421 18.062 18.2 21.766 6.004 25.934 1.244 27.561.828 27.778.874 28.61c.07 1.214.828 1.121 9.595-1.176 9.072-2.377 17.15-3.92 39.246-7.496C123.565 7.986 157.869 4.492 195.942 5.046c7.461.108 19.25 1.696 19.17 2.582-.107 1.183-7.874 4.31-25.75 10.366-21.992 7.45-35.43 12.534-36.701 13.884-2.173 2.308-.202 4.407 4.442 4.734 2.654.187 3.263.157 15.593-.78 35.401-2.686 57.944-3.488 88.365-3.143 46.327.526 75.721 2.23 130.788 7.584 19.787 1.924 20.814 1.98 24.557 1.332l.066-.011c1.201-.203 1.53-1.825.399-2.335-2.911-1.31-4.893-1.604-22.048-3.261-57.509-5.556-87.871-7.36-132.059-7.842-23.239-.254-33.617-.116-50.627.674-11.629.54-42.371 2.494-46.696 2.967-2.359.259 8.133-3.625 26.504-9.81 23.239-7.825 27.934-10.149 28.304-14.005.417-4.348-3.529-6-16.878-7.066Z" />
    </svg>
  );
}

function HighlightedHeading({ text, highlight, className, underlineFill, level = 'h2' }) {
  const Tag = level;
  if (!highlight) return <Tag className={className}>{text}</Tag>;
  const idx = text.indexOf(highlight);
  if (idx === -1) return <Tag className={className}>{text}</Tag>;
  const before = text.slice(0, idx);
  const after = text.slice(idx + highlight.length);
  return (
    <Tag className={className}>
      {before}
      <span className="relative whitespace-nowrap">
        <Underline fillClass={underlineFill} />
        <span className="relative">{highlight}</span>
      </span>
      {after}
    </Tag>
  );
}

const buttonBase = 'inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2';
const buttonOutlineBase = 'inline-flex items-center justify-center rounded-full ring-1 py-2 px-4 text-sm';

const SOLID_SLATE = 'bg-slate-900 text-white hover:bg-slate-700 hover:text-slate-100 active:bg-slate-800 active:text-slate-300 focus-visible:outline-slate-900 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 dark:active:bg-slate-300';
const SOLID_BLUE = 'bg-blue-600 text-white hover:bg-blue-500 hover:text-slate-100 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600';
const SOLID_WHITE = 'bg-white text-slate-900 hover:bg-blue-50 active:bg-blue-200 active:text-slate-600 focus-visible:outline-white';
const OUTLINE_SLATE = 'ring-slate-200 text-slate-700 hover:text-slate-900 hover:ring-slate-300 active:bg-slate-100 active:text-slate-600 focus-visible:outline-blue-600 focus-visible:ring-slate-300 dark:ring-slate-700 dark:text-slate-300 dark:hover:text-white dark:hover:ring-slate-500 dark:active:bg-slate-800 dark:active:text-slate-400';
const OUTLINE_WHITE = 'ring-slate-700 text-white hover:ring-slate-500 active:ring-slate-700 active:text-slate-400 focus-visible:outline-white';

function Button({ href, children, onClick, variant = 'solid', color = 'slate', className = '' }) {
  let classes;
  if (variant === 'outline') {
    classes = `${buttonOutlineBase} ${color === 'white' ? OUTLINE_WHITE : OUTLINE_SLATE}`;
  } else {
    classes = `${buttonBase} ${color === 'white' ? SOLID_WHITE : color === 'blue' ? SOLID_BLUE : SOLID_SLATE}`;
  }
  return (
    <a href={href} onClick={onClick} className={`${classes} ${className}`}>
      {children}
    </a>
  );
}

function ScreenshotCard({ children }) {
  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-xl shadow-blue-900/20 ring-1 ring-slate-900/10 dark:ring-white/10">
      {children}
    </div>
  );
}

function FeatureMedia({ media, frame = 'screenshot' }) {
  if (media.type === 'icon') {
    return (
      <div className={`flex h-full w-full items-center justify-center rounded-xl p-12 ${frame === 'darkPanel' ? 'bg-white/10 ring-1 ring-white/20' : 'bg-slate-50 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800'}`}>
        <PCOServicesIcon className="h-32 w-32" />
      </div>
    );
  }
  return (
    <ScreenshotCard>
      <img src={media.src} alt={media.alt} className="block w-full h-auto" loading="lazy" />
    </ScreenshotCard>
  );
}

function PrimaryFeaturesSection({ app }) {
  return (
    <section
      id={app.key}
      className="relative overflow-hidden bg-blue-600 pt-20 pb-28 sm:py-32"
      style={{
        backgroundImage: `url(${featuresGradient})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <Container className="relative">
        <div className="mx-auto max-w-2xl text-center xl:max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-200">{app.name}</p>
          <HighlightedHeading
            text={app.tagline}
            highlight={app.highlightWord}
            underlineFill="fill-white/40"
            className="mt-3 font-display text-3xl tracking-tight text-white sm:text-4xl md:text-5xl"
          />
        </div>
        <div className="mt-16 md:mt-20 space-y-16 lg:space-y-24">
          {app.features.map((feature, i) => (
            <div key={feature.title} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                <FeatureMedia media={feature.media} frame={feature.media.type === 'icon' ? 'darkPanel' : 'screenshot'} />
              </div>
              <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-white">{feature.title}</h3>
                <p className="mt-4 text-lg text-blue-100 leading-relaxed">{feature.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function SecondaryFeaturesSection({ app }) {
  return (
    <section id={app.key} className="pt-20 pb-14 sm:pt-32 sm:pb-20 lg:pb-32 bg-white dark:bg-slate-950">
      <Container>
        <div className="mx-auto max-w-2xl md:text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">{app.name}</p>
          <HighlightedHeading
            text={app.tagline}
            highlight={app.highlightWord}
            underlineFill="fill-blue-300/70"
            className="mt-3 font-display text-3xl tracking-tight text-slate-900 sm:text-4xl dark:text-white"
          />
        </div>
        <div className="mt-20 relative">
          <div className="overflow-hidden rounded-3xl lg:rounded-[2rem] bg-slate-200 dark:bg-slate-900 px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {app.features.map((feature) => (
                <div key={feature.title} className="flex flex-col">
                  <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 shadow-slate-900/5 ring-slate-500/10 dark:ring-white/10">
                    {feature.media.type === 'icon' ? (
                      <div className="flex h-48 w-full items-center justify-center bg-white">
                        <PCOServicesIcon className="h-24 w-24" />
                      </div>
                    ) : (
                      <img src={feature.media.src} alt={feature.media.alt} className="block w-full h-auto" loading="lazy" />
                    )}
                  </div>
                  <h3 className="mt-6 text-sm font-semibold text-blue-600 dark:text-blue-400">{feature.title}</h3>
                  <p className="mt-2 font-display text-xl tracking-tight text-slate-900 leading-snug dark:text-white">{feature.body.split('. ')[0]}.</p>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{feature.body.split('. ').slice(1).join(' ').trim() || ' '}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function FaqSection({ faqs: items }) {
  return (
    <section className="bg-white py-20 sm:py-32 dark:bg-slate-950">
      <Container>
        <h2 className="text-center font-display text-3xl tracking-tight text-slate-900 sm:text-4xl dark:text-white">
          Frequently asked questions
        </h2>
        <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-x-12 gap-y-10 md:grid-cols-2">
          {items.map((item) => (
            <div key={item.q}>
              <h3 className="font-display text-lg text-slate-900 dark:text-white">{item.q}</h3>
              <p className="mt-3 text-slate-600 dark:text-slate-400">{item.a}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function PullQuote({ quote }) {
  if (!quote) return null;
  return (
    <section className="py-20 sm:py-24 bg-white dark:bg-slate-950">
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-2xl sm:text-3xl tracking-tight text-slate-900 leading-snug font-medium dark:text-white">
            &ldquo;{quote.quote}&rdquo;
          </p>
          {(quote.name || quote.byline) && (
            <p className="mt-6 text-sm font-semibold text-slate-600 dark:text-slate-400">
              {quote.name}{quote.byline ? `, ${quote.byline}` : ''}
            </p>
          )}
        </div>
      </Container>
    </section>
  );
}

function CheckIcon({ className = '' }) {
  return (
    <svg aria-hidden="true" className={`h-6 w-6 flex-none fill-current stroke-current ${className}`}>
      <path d="M9.307 12.248a.75.75 0 1 0-1.114 1.004l1.114-1.004ZM11 15.25l-.557.502a.75.75 0 0 0 1.15-.043L11 15.25Zm4.844-5.041a.75.75 0 0 0-1.188-.918l1.188.918Zm-7.651 3.043 2.25 2.5 1.114-1.004-2.25-2.5-1.114 1.004Zm3.4 2.457 4.25-5.5-1.187-.918-4.25 5.5 1.188.918Z" strokeWidth={0} />
      <circle cx={12} cy={12} r={8.25} fill="none" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PricingPlan({ name, description, tiers, features, featured = false, onCtaClick, ctaKey }) {
  const headlineTier = tiers && tiers[0];
  const featureItems = (features && features.length) ? features : [];
  return (
    <section className={`flex flex-col rounded-3xl px-6 sm:px-8 ${featured ? 'order-first bg-blue-600 py-8 lg:order-none' : 'lg:py-8'}`}>
      {featured && (
        <span className="order-first mb-2 inline-flex w-fit items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          Most popular
        </span>
      )}
      <p className="order-first font-display text-5xl font-light tracking-tight text-white">{name}</p>
      <h3 className="mt-5 font-display text-lg text-white">
        {headlineTier ? headlineTier.formatted : '—'}
      </h3>
      <p className={`mt-2 text-base ${featured ? 'text-white' : 'text-slate-400'}`}>{description || 'Cloud-based tooling for your church.'}</p>
      <ul role="list" className={`order-last mt-10 flex flex-col gap-y-3 text-sm ${featured ? 'text-white' : 'text-slate-200'}`}>
        {featureItems.map((feature, i) => (
          <li key={i} className="flex">
            <CheckIcon className={featured ? 'text-white' : 'text-slate-400'} />
            <span className="ml-4">{feature}</span>
          </li>
        ))}
        {featureItems.length === 0 && (
          <li className="flex"><span className="ml-4 italic">Features coming soon</span></li>
        )}
      </ul>
      <Button
        href="/accounts/new"
        onClick={() => onCtaClick && onCtaClick(ctaKey)}
        variant={featured ? 'solid' : 'outline'}
        color="white"
        className="mt-8"
      >
        Start 30-day free trial
      </Button>
      <p className={`mt-3 text-xs text-center ${featured ? 'text-blue-100' : 'text-slate-400'}`}>
        30 days, no credit card required
      </p>
    </section>
  );
}

function PricingSection({ onCtaClick }) {
  const { loading, error, byApp } = usePricing();

  const buildPlan = (appKey, label, featured) => {
    const products = byApp[appKey] || [];
    const product = products[0];
    const tiers = product ? product.tiers : [];
    const features = product ? product.features : [];
    return (
      <PricingPlan
        key={appKey}
        name={label}
        description={product && product.description}
        tiers={tiers}
        features={features}
        featured={featured}
        onCtaClick={onCtaClick}
        ctaKey={`pricing_${appKey}`}
      />
    );
  };

  return (
    <section id="pricing" aria-label="Pricing" className="bg-slate-900 py-20 sm:py-32">
      <Container>
        <div className="md:text-center">
          <h2 className="font-display text-3xl tracking-tight text-white sm:text-4xl">
            Simple pricing, for every team.
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            {loading ? 'Loading live pricing…' : error ? 'Pricing temporarily unavailable.' : 'Subscribe to one app, or both. Cancel anytime.'}
          </p>
          <p className="mt-2 text-sm text-slate-400">
            Less than one ProPresenter license per month — no per-machine fees.
          </p>
        </div>
        <div className="-mx-4 mt-16 grid max-w-2xl grid-cols-1 gap-y-10 sm:mx-auto lg:-mx-8 lg:max-w-none lg:grid-cols-2 xl:mx-0 xl:gap-x-8">
          {buildPlan('slides', 'Slides', true)}
          {buildPlan('billboard', 'Billboard', false)}
        </div>
      </Container>
    </section>
  );
}

export default function HomeControl({ onCtaClick = () => {}, links = [] }) {
  const [slidesApp, billboardApp] = apps;
  const heroVariant = useExperiment('home_hero_copy');

  const handleHeroPrimaryClick = () => {
    onCtaClick('hero_primary');
    trackExperiment('home_hero_copy', heroVariant, 'experiment_click', { cta_label: 'hero_primary' });
  };

  return (
    <div className="bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="py-10">
        <Container>
          <nav className="relative z-50 flex justify-between">
            <a href="/" className="flex items-center" aria-label="WorshipKit home">
              <img src="/wk_logo_accounts.png" alt="WorshipKit" className="h-10 w-auto invert dark:invert-0" />
            </a>
            <div className="flex items-center gap-x-5 md:gap-x-8">
              {links.map((link, idx) => {
                const isLast = idx === links.length - 1;
                return isLast ? (
                  <Button key={link.url} href={link.url} onClick={() => onCtaClick(`nav_${link.text.toLowerCase().replace(/\s+/g, '_')}`)} color="blue">
                    {link.text}
                  </Button>
                ) : (
                  <a key={link.url} href={link.url} className="hidden md:inline-block rounded-lg px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white">
                    {link.text}
                  </a>
                );
              })}
            </div>
          </nav>
        </Container>
      </header>

      <section className="pt-20 pb-16 text-center lg:pt-32">
        <Container>
          {heroVariant === 'treatment' ? (
            <>
              <HighlightedHeading
                text={hero.treatment.headline}
                highlight={hero.treatment.highlightWord}
                underlineFill="fill-blue-300/70"
                level="h1"
                className="mx-auto max-w-4xl font-display text-5xl font-medium tracking-tight text-slate-900 sm:text-7xl leading-[1.05] dark:text-white"
              />
              <p className="mx-auto mt-6 max-w-2xl text-lg tracking-tight text-slate-700 dark:text-slate-300">{hero.treatment.subhead}</p>
            </>
          ) : (
            <>
              <h1 className="mx-auto max-w-4xl font-display text-5xl font-medium tracking-tight text-slate-900 sm:text-7xl leading-[1.05] dark:text-white">
                Collaborate and edit slides from{' '}
                <span className="relative whitespace-nowrap text-blue-600 dark:text-blue-400">
                  <Underline />
                  <span className="relative">anywhere</span>
                </span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg tracking-tight text-slate-700 dark:text-slate-300">{hero.subhead}</p>
              <p className="mx-auto mt-4 max-w-2xl text-base text-slate-500 leading-relaxed dark:text-slate-400">{hero.preamble}</p>
            </>
          )}
          <div className="mt-10 flex justify-center gap-x-6">
            <Button href={hero.primaryCta.href} onClick={handleHeroPrimaryClick}>
              {hero.primaryCta.text}
            </Button>
            <Button href={hero.secondaryCta.href} onClick={() => onCtaClick('hero_secondary')} variant="outline">
              {hero.secondaryCta.text}
            </Button>
          </div>
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">30 days, no credit card required</p>
          {heroVariant === 'treatment' && (
            <div className="mx-auto mt-16 max-w-4xl">
              <ScreenshotCard>
                <img src={hero.demoImage} alt="WorshipKit Slides document review" className="block w-full h-auto" loading="lazy" />
              </ScreenshotCard>
            </div>
          )}
        </Container>
      </section>

      <PrimaryFeaturesSection app={slidesApp} />
      <SecondaryFeaturesSection app={billboardApp} />

      <FaqSection faqs={faqs} />

      <PullQuote quote={testimonial} />

      <PricingSection onCtaClick={onCtaClick} />

      <section
        id="get-started-today"
        className="relative overflow-hidden bg-blue-600 py-32"
        style={{
          backgroundImage: `url(${ctaGradient})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        <Container className="relative">
          <div className="mx-auto max-w-lg text-center">
            <h2 className="font-display text-3xl tracking-tight text-white sm:text-4xl">{closingCta.headline}</h2>
            <p className="mt-4 text-lg tracking-tight text-white">{closingCta.body}</p>
            <Button href={closingCta.primaryCta.href} onClick={() => onCtaClick('footer_primary')} color="white" className="mt-10">
              {closingCta.primaryCta.text}
            </Button>
          </div>
        </Container>
      </section>

      <section className="bg-white py-10 dark:bg-slate-950">
        <Container className="flex flex-col items-center gap-6">
          <nav aria-label="Footer" className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <a href="/help" className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
              Help
            </a>
            <a href="/vs/proclaim" className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
              vs Proclaim
            </a>
            <a href="/vs/easyworship" className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
              vs EasyWorship
            </a>
          </nav>
          <ProductHuntBadge theme="light" />
        </Container>
      </section>
    </div>
  );
}
